import React, {
  useState,
  useEffect,
  forwardRef,
  useMemo,
  memo,
  useCallback,
  useRef,
  useTransition,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { AgCharts } from "ag-charts-react";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  selectCurrentTab,
  setQuerySubType,
} from "../../../redux/queryBuilderSlice";
import { countMeasureNodes } from "../../../utils/util";

interface QueryChartProps {
  data: any;
  subType: any;
  currentTabIndex: number;
  isError: boolean;
}

type ChartType = "bar" | "line" | "area" | "pie";

const COLORS_LIST: string[] = [
  "#B92051",
  "#501555",
  "#006D89",
  "#125E1F",
  "#1D3BAA",
  "#54BAA0",
  "#566CBF",
  "#C94100",
  "#9D5700",
  "#EC8200",
  "#00495B",
  "#CD6083",
  "#6B0F01",
  "#E8AE92",
  "#9DCEA6",
  "#F1A649",
  "#B280B6",
];

const DROPDOWN_OPTIONS = [
  { label: "Bar Chart", value: "bar" },
  { label: "Line Chart", value: "line" },
  { label: "Pie Chart", value: "pie" },
  { label: "Area Chart", value: "area" },
];

const MAX_WIDTH = 700;
const CHART_HEIGHT = 450;
const PIE_LEGEND_WIDTH = 200;
const BAR_WIDTH = 15;

const QueryChart = forwardRef<HTMLDivElement, QueryChartProps>(
  ({ data, subType, currentTabIndex, isError }, chartRef) => {
    const dispatch = useDispatch<AppDispatch>();
    const currTabData = useSelector(selectCurrentTab);
    const flattenedResults = useSelector(
      (state: RootState) => currTabData.results,
    );

    const [selectedChart, setSelectedChart] = useState<ChartType>(
      subType as ChartType,
    );
    const [pieSelectedMeasure, setPieSelectedMeasure] = useState<string>("");

    // useTransition defers the heavy canvas repaint so the browser can paint
    // the dropdown selection first — this is the main fix for the lag.
    const [, startTransition] = useTransition();

    // Keep a stable ref to the current effective measure so pieTooltipRenderer
    // never needs to change identity. Without this, every measure change
    // recreates the renderer → chartOptions rebuilds → full canvas repaint.
    const pieSelectedMeasureRef = useRef(pieSelectedMeasure);

    const pieTooltipRenderer = useCallback(
      (params: any) => {
        return `<div style="padding:8px">
          <b>${params.datum["xFormatted"]}</b><br/>
          ${params.datum[pieSelectedMeasureRef.current]?.toLocaleString(
            "en-US",
            {
              maximumFractionDigits: 0,
            },
          )}
        </div>`;
      },
      [], // stable — reads from ref, no identity change on measure selection
    );

    useEffect(() => {
      setSelectedChart(subType);
    }, [subType]);

    // useEffect(() => {
    //   if (selectedChart) {
    //     dispatch(setQuerySubType({ type: selectedChart, currentTabIndex }));
    //   }
    // }, [currentTabIndex]);

    const handleChartChange = useCallback(
      (newValue: ChartType) => {
        // startTransition: dropdown visual feedback is urgent; canvas repaint is not.
        startTransition(() => {
          setSelectedChart(newValue);
          dispatch(setQuerySubType({ type: newValue, currentTabIndex }));
        });
      },
      [dispatch, currentTabIndex],
    );

    const handleMeasureChange = useCallback((newValue: string) => {
      startTransition(() => {
        setPieSelectedMeasure((currentValue) =>
          currentValue === newValue ? currentValue : newValue,
        );
      });
    }, []);

    const { processedData, yKeyValues } = useMemo(() => {
      if (!flattenedResults) {
        return { processedData: [], yKeyValues: [] };
      }

      const xKeys: string[] = [];
      const yKeyValues: { field: string; name: string }[] = [];

      const processHeader = (header: any, parentName = "") => {
        const combinedName = parentName
          ? `${parentName}, ${header.headerName}`
          : header.headerName;

        if (header.children?.length > 0) {
          header.children.forEach((child: any) =>
            processHeader(child, combinedName),
          );
        } else {
          yKeyValues.push({
            field: header.field,
            name: combinedName,
          });
        }
      };

      const rows = flattenedResults.query.axes.ROWS;

      flattenedResults.result.headers.forEach((header: any) => {
        if (rows.includes(header.levelUniqueName)) {
          xKeys.push(header.field);
        } else {
          processHeader(header);
        }
      });

      const baseData = flattenedResults.result.rows || [];
      const updatedData = baseData.map((row: any) => {
        const updatedRow: any = {
          ...row,
          xFormatted: xKeys.map((key) => row?.[key] ?? "").join(","),
        };
        yKeyValues.forEach((yKey) => {
          updatedRow[yKey.name] = row?.[yKey.field];
        });

        return updatedRow;
      });

      return { processedData: updatedData, yKeyValues };
    }, [flattenedResults, data]);
    const measureOptions = useMemo(() => {
      return yKeyValues.map((yKey) => ({
        label: yKey.name,
        value: yKey.name,
      }));
    }, [yKeyValues]);

    // Initialize pieSelectedMeasure synchronously to avoid double-render
    const effectivePieMeasure = useMemo(() => {
      if (selectedChart === "pie" && measureOptions.length > 0) {
        if (
          !pieSelectedMeasure ||
          !measureOptions.some((o) => o.value === pieSelectedMeasure)
        ) {
          return measureOptions[0].value;
        }
      }
      return pieSelectedMeasure;
    }, [selectedChart, measureOptions, pieSelectedMeasure]);

    // Direct assignment during render: tooltip always reads the correct value
    // immediately without the stale window a useEffect would create.
    pieSelectedMeasureRef.current = effectivePieMeasure;

    const chartWidth = useMemo(() => {
      return Math.max(MAX_WIDTH, BAR_WIDTH * processedData.length);
    }, [processedData.length]);

    const cellCount = useMemo(() => {
      return processedData.reduce((acc: number, row: any) => {
        return acc + (row ? Object.keys(row).length : 0);
      }, 0);
    }, [processedData]);

    const isPieDataValid = useMemo(() => {
      if (selectedChart !== "pie") return true;
      //   const isMultipleTimePeriods =
      //     flattenedResults.result.headers.filter((item: any) =>
      //       item.levelUniqueName.includes("Time Period"),
      //     ).length > 0;
      //   if (isMultipleTimePeriods && selectedChart == "pie") {
      //     return false;
      //   }
      return processedData.some((row: any) => row.xFormatted !== "");
    }, [selectedChart, processedData]);

    // Guard against mismatched data during tab switches — if the chart keys
    // don't exist in processedData (stale props from the previous tab),
    // AG Charts can hang trying to render NaN/undefined values.
    const hasValidSeriesData = useMemo(() => {
      if (processedData.length === 0 || yKeyValues.length === 0) return false;
      if (selectedChart === "pie") {
        if (!effectivePieMeasure) return false;
        return processedData.some(
          (row: any) =>
            row[effectivePieMeasure] !== undefined &&
            row[effectivePieMeasure] !== null &&
            !isNaN(Number(row[effectivePieMeasure])),
        );
      }
      return yKeyValues.some((yKey) =>
        processedData.some(
          (row: any) => row[yKey.name] !== undefined && row[yKey.name] !== null,
        ),
      );
    }, [processedData, yKeyValues, selectedChart, effectivePieMeasure]);

    const rowCount =
      flattenedResults?.result?.mergeCount?.rowCount ?? processedData.length;
    const pieRowLimitExceeded = selectedChart === "pie" && rowCount > 50;

    const tooLargeDataset = processedData.length > 5000 || cellCount > 20000;

    const multiMeasureSelected =
      selectedChart === "pie" &&
      flattenedResults?.result?.headers &&
      countMeasureNodes(flattenedResults.result.headers) > 1;

    const canRenderChart =
      !isError &&
      processedData.length > 0 &&
      !tooLargeDataset &&
      !(selectedChart === "pie" && !isPieDataValid) &&
      !pieRowLimitExceeded &&
      !multiMeasureSelected &&
      hasValidSeriesData;
    const chartOptions: any = useMemo(() => {
      if (!canRenderChart) return null;
      if (selectedChart === "pie") {
        return {
          data: processedData,
          height: CHART_HEIGHT,
          animation: { enabled: false },
          series: [
            {
              type: "pie",
              angleKey: effectivePieMeasure,
              legendItemKey: "xFormatted",
              fills: COLORS_LIST,
              tooltip: {
                renderer: pieTooltipRenderer,
              },
            },
          ],
          legend: {
            enabled: true,
            position: "right",
            layout: "vertical",
            width: PIE_LEGEND_WIDTH,
          },
        };
      }

      const series = yKeyValues.map((yKey, index) => ({
        type: selectedChart,
        xKey: "xFormatted",
        yKey: yKey.name,
        fill: COLORS_LIST[index % COLORS_LIST.length],
        stroke: COLORS_LIST[index % COLORS_LIST.length],
        marker: {
          fill: COLORS_LIST[index % COLORS_LIST.length],
          stroke: COLORS_LIST[index % COLORS_LIST.length],
        },
        tooltip: {
          renderer: (params: any) => {
            return `<div style="padding:8px">
                            <b>${params.datum["xFormatted"]}</b><br/>
                            ${params.datum[yKey.name]?.toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })}
                        </div>`;
          },
        },
      }));

      return {
        data: processedData,
        height: CHART_HEIGHT,
        animation: { enabled: false },
        series,
        axes: [
          {
            type: "category",
            position: "bottom",
            label: { rotation: -90, fontSize: 10 },
          },
          { type: "number", position: "left" },
        ],
        legend: { enabled: true },
      };
    }, [
      canRenderChart,
      processedData,
      selectedChart,
      effectivePieMeasure,
      yKeyValues,
      pieTooltipRenderer,
    ]);

    return (
      <div className="charts" style={{ overflow: "auto" }}>
        <div className="chart-dropdown">
          <Dropdown
            id={"charts-dropdown"}
            options={DROPDOWN_OPTIONS}
            selectedValue={selectedChart}
            onChange={handleChartChange}
          />
        </div>

        {selectedChart === "pie" && measureOptions.length > 1 && !isError && (
          <div className="measure-dropdown">
            <Dropdown
              id={"measures-dropdown"}
              options={measureOptions}
              selectedValue={effectivePieMeasure}
              onChange={handleMeasureChange}
            />
          </div>
        )}

        {processedData && (processedData.length > 5000 || cellCount > 20000) ? (
          <div className="chart-message">
            <span>
              The data set is too large for visualisation. Kindly apply filters
              to reduce the data size.
            </span>
          </div>
        ) : (
          chartOptions && (
            <div
              ref={chartRef}
              className="ag-theme-charts"
              style={{
                borderBottom: "1px solid grey",
                width: selectedChart === "pie" ? MAX_WIDTH : chartWidth,
                margin: "auto",
              }}
            >
              <AgCharts options={chartOptions} />
            </div>
          )
        )}

        {multiMeasureSelected && (
          <div style={{ paddingLeft: 10 }}>
            Results exceed the permitted limit. Remove selection from measure
            axis.
          </div>
        )}

        {pieRowLimitExceeded && (
          <div style={{ paddingLeft: 10 }}>
            Results exceed the permitted limit. Too many rows are selected,
            maximun 50 rows allowed.
          </div>
        )}

        {/* {selectedChart === "pie" && !isPieDataValid && (
          <span>Remove selection from column axis</span>
        )} */}
      </div>
    );
  },
);

export default memo(QueryChart);
